/**
 * SplitDiffBoard — side-by-side diff view.
 *
 * Two `BoardCanvas` panes (before / after) underneath a single shared
 * `BoardChrome`. Pan/zoom is shared by handing BOTH panes the same
 * `ViewportStore`: a gesture on either pane mutates the one store and both
 * re-render from it — no per-frame mirror callbacks, no ping-pong guards,
 * no `setViewportImmediate`. Only the AFTER pane binds the global keyboard
 * shortcuts (otherwise each keypress would apply twice to the store).
 * Hover and selection are mirrored separately below.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeSet } from "@archlang/engine";
import type { DependencyEdge, Module } from "@archlang/scene/arch-model";
import { DiffProvider } from "./DiffContext.js";
import { GhostLinks } from "./ghostLinks.js";
import {
  BoardCanvas,
  BreadcrumbHud,
  createViewportStore,
  useViewportValue,
  type BoardCanvasHandle,
} from "../board/index.js";
import { flattenModulesWithParent } from "@archlang/scene/arch-model";
import { createBoardEngine, initialViewport, walkFocal } from "@archlang/scene/board";
import { NodeColoringProvider, type NodeColoring } from "../board/coloring/index.js";
import { buildDiffColoring } from "../board/coloring/adapters.js";

export interface SplitDiffBoardProps {
  readonly before: readonly Module[];
  readonly after: readonly Module[];
  readonly beforeEdges: readonly DependencyEdge[];
  readonly afterEdges: readonly DependencyEdge[];
  readonly changeSet: ChangeSet;
  /** Node colouring override (stroke + minimap). Default: diff colouring built
   *  from the union of before+after — pass one to share it with outer chrome
   *  (Studio's legend hover/pin builds it once upstream). */
  readonly coloring?: NodeColoring;
  /** Board node ids to highlight (legend hover/pin). */
  readonly memberHighlightIds?: readonly string[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string | null) => void;
}

function spaceFromModules(modules: readonly Module[], edges: readonly DependencyEdge[]) {
  // Same lazy engine the live board uses; a full-depth request yields a board
  // tree byte-identical to the old eager `buildBoardFromModules` (guarded by
  // engine.test.ts), so the diff panes get one board-build path, not two.
  const flat = flattenModulesWithParent(modules);
  return createBoardEngine(flat, edges).getSpace({ path: [], depth: Infinity });
}

export function SplitDiffBoard({
  before,
  after,
  beforeEdges,
  afterEdges,
  changeSet,
  coloring,
  memberHighlightIds,
  selectedId,
  onSelect,
}: SplitDiffBoardProps) {
  const beforeSpace = useMemo(() => spaceFromModules(before, beforeEdges), [before, beforeEdges]);
  const afterSpace = useMemo(() => spaceFromModules(after, afterEdges), [after, afterEdges]);
  const allModules = useMemo(() => [...before, ...after], [before, after]);
  const fallbackColoring = useMemo(
    () => (coloring ? null : buildDiffColoring(allModules, changeSet)),
    [coloring, allModules, changeSet],
  );
  const nodeColoring = coloring ?? fallbackColoring!;
  const ref = useRef<HTMLDivElement>(null);
  const afterPaneRef = useRef<HTMLDivElement>(null);
  const beforeRef = useRef<BoardCanvasHandle>(null);
  const afterRef = useRef<BoardCanvasHandle>(null);

  // One viewport store shared by both panes. Created once; the panes read
  // and write it directly, so they stay in lock-step for free.
  const storeRef = useRef<ReturnType<typeof createViewportStore> | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createViewportStore({
      focalPath: [afterSpace.id],
      center: { x: afterSpace.originX + afterSpace.width / 2, y: afterSpace.originY + afterSpace.height / 2 },
      zoom: 1,
    });
  }
  const store = storeRef.current;
  const viewport = useViewportValue(store);

  // ---- Initial fit (the store owner's job) -----------------------------
  // Re-seed when the diff changes. Deferred a frame so the pane has laid
  // out and `getBoundingClientRect` returns its real size.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const el = afterPaneRef.current;
      if (!el) return;
      const { width, height } = el.getBoundingClientRect();
      if (width === 0 || height === 0) return;
      store.set(initialViewport(afterSpace, width, height));
      setSeeded(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [beforeSpace, afterSpace, store]);

  // ---- Hover mirror ----------------------------------------------------
  const hoverIdRef = useRef<string | null>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const setHover = (id: string | null) => {
      if (hoverIdRef.current === id) return;
      root
        .querySelectorAll<HTMLElement>('[data-cross-hover="true"]')
        .forEach((el) => el.removeAttribute("data-cross-hover"));
      hoverIdRef.current = id;
      if (!id) return;
      root
        .querySelectorAll<HTMLElement>(`[data-module-id="${CSS.escape(id)}"]`)
        // Skip gravity-well context-layer copies (dimmed parent surround) — they
        // share module ids with the focal widgets but aren't the hovered node.
        .forEach((el) => {
          if (el.closest("[data-board-context]")) return;
          el.setAttribute("data-cross-hover", "true");
        });
    };
    const onMove = (e: PointerEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-module-id]");
      const target = el && !el.closest("[data-board-context]") ? el : null;
      setHover(target?.dataset.moduleId ?? null);
    };
    const onLeave = () => setHover(null);
    root.addEventListener("pointermove", onMove);
    root.addEventListener("pointerleave", onLeave);
    return () => {
      root.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      setHover(null);
    };
  }, []);

  // Chrome reads breadcrumb steps off the AFTER pane's space — pan/zoom
  // are shared so the focal path is the same on both sides.
  const steps = useMemo(
    () => walkFocal(afterSpace, viewport.focalPath),
    [afterSpace, viewport],
  );

  // Chrome actions go through the AFTER pane's handle — it owns the
  // keyboard-authoritative controls and writes the shared store, so the
  // before pane follows automatically.
  const onResetView = useCallback(() => afterRef.current?.resetView(), []);
  const onNavigateTo = useCallback((path: readonly string[]) => afterRef.current?.navigateTo(path), []);

  return (
    <DiffProvider changeSet={changeSet}>
      <NodeColoringProvider value={nodeColoring}>
      <div ref={ref} className="relative grid grid-cols-2 h-full gap-px bg-slate-400/[0.12]">
        <div className="relative overflow-hidden bg-slate-900" data-side="before">
          <BoardCanvas
            ref={beforeRef}
            root={beforeSpace}
            viewportStore={store}
            keyboardNav={false}
            // One minimap for the split, on the right pane: the panes share a
            // viewport, so a second one (plus its zoom readout and dev chips)
            // showed the same rectangle twice, mid-board where the panes meet.
            hideMinimap
            selectedId={selectedId}
            memberHighlightIds={memberHighlightIds}
            onSelect={onSelect}
          />
        </div>
        <div ref={afterPaneRef} className="relative overflow-hidden bg-slate-900" data-side="after">
          <BoardCanvas
            ref={afterRef}
            root={afterSpace}
            viewportStore={store}
            keyboardNav
            selectedId={selectedId}
            memberHighlightIds={memberHighlightIds}
            onSelect={onSelect}
          />
        </div>
        {seeded && (
          <>
            {/* Top-left breadcrumb spans both panes. */}
            <div className="absolute top-3 left-3">
              <BreadcrumbHud
                steps={steps}
                onNavigate={(i) => {
                  // Root → reset; any other crumb (incl. current) → re-frame.
                  if (i === 0) onResetView();
                  else onNavigateTo(viewport.focalPath.slice(0, i + 1));
                }}
              />
            </div>
          </>
        )}
        <GhostLinks containerRef={ref} />
      </div>
      </NodeColoringProvider>
    </DiffProvider>
  );
}
